import { useQueries } from '@tanstack/react-query';
import { ApiError, SourcingCampaignIndex } from 'api-clients/monolith';
import { useRef } from 'react';

import { campaignsDateParams } from '@/api-clients/campaigns-date-params';
import {
  getReachBreakdown,
  ReachBreakdown,
} from '@/api-clients/get-reach-breakdown';
import { DateRangeFilter } from '@/contexts/table-context';
import { queryKeys } from '@/query-keys';

export type ReachBreakdownState = {
  status: 'loading' | 'ready' | 'failed' | 'absent';
  data?: ReachBreakdown;
};

export function useReachBreakdowns(
  campaigns: SourcingCampaignIndex[],
  dateRange: DateRangeFilter,
  localDays: boolean,
  enabled: boolean,
  expandedIds: ReadonlySet<string>,
): Map<string, ReachBreakdownState> {
  const inFlight = useRef(new Set<Promise<ReachBreakdown>>());
  const rows = enabled
    ? campaigns.filter(
        row => row.sourcing_channel.slug === 'reach' && expandedIds.has(row.id),
      )
    : [];
  const dates = campaignsDateParams(dateRange, localDays);
  const results = useQueries({
    queries: rows.map(row => ({
      queryKey: queryKeys.reachBreakdown(row.id, dates),
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const active = inFlight.current;
        while (active.size >= 4) {
          // A failed neighbor releases its slot without failing this row.
          await Promise.race(
            [...active].map(request => request.catch(() => {})),
          );
          signal.throwIfAborted();
        }
        signal.throwIfAborted();
        const request = getReachBreakdown(row.id, dates);
        const cancel = () => request.cancel();
        signal.addEventListener('abort', cancel, { once: true });
        active.add(request);
        try {
          return await request;
        } finally {
          signal.removeEventListener('abort', cancel);
          active.delete(request);
        }
      },
      retry: (n: number, error: ApiError) =>
        n < 1 &&
        (error instanceof TypeError ||
          (error.status >= 500 && error.status < 600)),
    })),
  });

  return new Map(
    rows.map((row, index): [string, ReachBreakdownState] => {
      const result = results[index];
      if (
        result.isError &&
        'status' in result.error &&
        result.error.status === 404
      ) {
        return [row.id, { status: 'absent', data: undefined }];
      }
      return [
        row.id,
        {
          status: result.isError ? 'failed' : result.data ? 'ready' : 'loading',
          data: result.data,
        },
      ];
    }),
  );
}
